'use client';
import { useContext } from 'react';
import { clsx } from 'clsx';
import Image, { type ImageProps } from 'components/UI/Image/Image';
import Link, { type LinkProps } from 'components/UI/Link/Link';
import { AppContext } from 'context/AppContext';
import type { PropsWithClassName } from 'types/react-props';
import { getRefreshSizes } from 'utils/image';
import { formatMillisecondsToMinutes } from 'utils/time';
import { linkUrl } from 'utils/url';
import { Icon } from '../Icon';
import { RtlPlusLogo } from '../Logo';
import { SponsoredIcon } from '../SponsoredIcon';
import styles from './Teaser.module.scss';

export type TeaserVariant = 'small' | 'medium' | 'large' | 'color' | 'search';

export type TeaserProps = PropsWithClassName<{
  readonly id?: string;
  readonly title: string;
  readonly linkHref: LinkProps['href'];
  readonly openLinkInNewTab?: boolean;
  readonly image?: ImageProps;
  readonly subtitle?: string;
  readonly variant?: TeaserVariant;
  readonly subtitleColor?: string;
  readonly backgroundColor?: string;
  readonly isSponsored?: boolean;
  readonly onClick?: () => void;
  readonly isGallery?: boolean;
  readonly isVideo?: boolean;
  readonly isHighlight?: boolean;
  readonly isRtlPlus?: boolean;
  readonly galleryCount?: number;
  readonly videoDuration?: number;
  readonly disableLazyLoading?: boolean;
}>;

export const getTeaserSizes = (variant: string) => {
  switch (variant) {
    case 'color':
      return getRefreshSizes('teaserColor');
    case 'large':
      return getRefreshSizes('teaserLarge');
    case 'small':
      return getRefreshSizes('teaserSmall');
    case 'medium':
    default:
      return getRefreshSizes('teaserMedium');
  }
};

const Teaser = ({
  id,
  title,
  subtitle,
  linkHref,
  openLinkInNewTab = false,
  image,
  variant = 'medium',
  subtitleColor,
  backgroundColor,
  isSponsored = false,
  isGallery = false,
  isVideo = false,
  isHighlight = false,
  isRtlPlus = false,
  galleryCount,
  videoDuration,
  className,
  disableLazyLoading,
}: TeaserProps) => {
  const {
    pageMetaData: { isStoryline },
  } = useContext(AppContext);

  const DynamicTag = isStoryline ? 'div' : 'h3';

  return (
    <div
      className={clsx(styles.teaser, className, {
        [styles.small]: variant === 'small',
        [styles.medium]: variant === 'medium',
        [styles.large]: variant === 'large',
        [styles.color]: variant === 'color',
        [styles.highlight]: isHighlight,
      })}
      data-id={variant === 'large' ? 'lead' : undefined}
      style={{ backgroundColor: variant === 'color' ? (backgroundColor ?? '#000') : undefined }}
    >
      <div className={styles.imageWrapper}>
        <Image
          className={styles.image}
          image={image?.image}
          cropOverride={variant === 'large' || variant === 'color' ? 'c16_9' : 'c3_4'}
          alt={image?.alt}
          width={image?.width}
          src={image?.src}
          sizes={getTeaserSizes(variant)}
          fallback={{ className: styles.image }}
          priority={disableLazyLoading}
        />
        {isSponsored && <SponsoredIcon className={styles.sponsored} />}
        {isGallery && variant === 'small' && (
          <div className={styles['gallery-count']}>
            <Icon className={styles.iconGallery} name="gallery" size={16} />
            {galleryCount && <span>{galleryCount} Bilder</span>}
          </div>
        )}
        {isVideo && variant === 'small' && (
          <div className={styles.button}>
            <Icon className={styles.iconPlay} name="play" size={16} />
            {!!videoDuration && formatMillisecondsToMinutes(videoDuration, '', false)}
          </div>
        )}
      </div>
      <div className={clsx(styles.contentWrapper, { [styles.paddingBottom]: !isVideo && !isRtlPlus })}>
        <Link
          id={id}
          className={styles.link}
          href={linkUrl(linkHref ?? '', undefined, !!isVideo)}
          target={openLinkInNewTab || isRtlPlus ? '_blank' : undefined}
          data-tracking-id="teaser-link"
        >
          <DynamicTag className={styles.title}>
            {isHighlight ? <span style={{ backgroundColor: subtitleColor ?? undefined }}>{title}</span> : title}
          </DynamicTag>
        </Link>
        {!!subtitle && !isRtlPlus && (
          <div
            className={styles.subtitle}
            style={{
              backgroundColor:
                variant !== 'small' && variant !== 'color' && !!subtitleColor ? subtitleColor : undefined,
            }}
          >
            {subtitle}
          </div>
        )}
        {(isVideo || isRtlPlus) && variant !== 'small' && (
          <div className={clsx(styles.button, { [styles.rtlPlus]: isRtlPlus })}>
            <Icon className={styles.iconPlay} name="play" size={20} />
            {isVideo && (
              <span>
                Video {!!videoDuration && `[ ${formatMillisecondsToMinutes(videoDuration, '', false)} ]`} • Jetzt
                ansehen
              </span>
            )}
            {isRtlPlus && (
              <>
                <span>
                  Jetzt <strong>auf RTL+</strong> streamen
                </span>
                <Icon className={styles.iconExternalLink} name="externalLink" size={16} />
              </>
            )}
          </div>
        )}
      </div>
      {isGallery && variant !== 'small' && (
        <div className={styles['gallery-count']}>
          <Icon name="gallery" size={16} />
          {galleryCount && <span>{galleryCount} Bilder</span>}
        </div>
      )}
      {isRtlPlus && variant !== 'small' && (
        <RtlPlusLogo className={styles.rtlPlusLogo} colors={['#ff5c01', '#d4000d', '#6e0ced']} />
      )}
    </div>
  );
};

export default Teaser;
